import React, { useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import moment from "moment";
import func from "../../services/functions";
import {
  formatMoney,
  formatStatusLabel,
  getRowCurrency,
  getStatusTone,
  isCurrencyField,
  isIdentifierField,
  isStatusField,
} from "./chatbotFormatters";

const hiddenFields = ["iic", "erpId", "id"];

const knownLabels = {
  buyerName: "Buyer",
  buyerNuis: "NUIS",
  sellerName: "Seller",
  sellerNuis: "Seller NUIS",
  recordDate: "Record Date",
  issueDate: "Issue Date",
  ublId: "Invoice Number",
  cisStatus: "Status",
  internalStatus: "Status",
  currency: "Currency",
  totalInCurrency: "Total",
  total: "Total",
  fic: "NIVF",
};

const inferColumnType = (key, value) => {
  const keyLower = key.toLowerCase();

  if (keyLower.includes("time")) {
    return "datetime";
  }
  if (keyLower.includes("date")) {
    return "date";
  }
  if (isStatusField(key)) {
    return "enum";
  }
  if (isCurrencyField(key)) {
    return "currency";
  }
  if (
    keyLower.includes("total") ||
    keyLower.includes("amount") ||
    keyLower.includes("price") ||
    keyLower.includes("tax") ||
    keyLower.includes("vat")
  ) {
    return "money";
  }
  if (typeof value === "number" && value > 1000000000000) {
    return "date";
  }
  if (typeof value === "boolean") {
    return "boolean";
  }
  return "string";
};

const resolveColumnType = (column, value) => {
  if (isStatusField(column.key)) return "enum";
  if (isCurrencyField(column.key)) return "currency";
  const declaredType = String(column.type || "").toLowerCase();
  if (declaredType === "bigdecimal" || declaredType === "decimal") return "money";
  if (["timestamp", "localdatetime", "offsetdatetime"].includes(declaredType)) {
    return "datetime";
  }
  return declaredType || inferColumnType(column.key, value);
};

const getColumnClass = (column) => {
  if (column.type === "money") return "chatbot-column-money";
  if (column.type === "enum") return "chatbot-column-status";
  if (column.type === "currency") return "chatbot-column-currency";
  if (isIdentifierField(column.key)) return "chatbot-column-identifier";
  return "";
};

const getColumnStyle = (column) => {
  if (column.type === "money") return { minWidth: "130px" };
  if (column.type === "date" || column.type === "datetime") return { minWidth: "118px" };
  if (column.type === "enum") return { minWidth: "120px" };
  if (column.type === "currency") return { minWidth: "86px" };
  if (isIdentifierField(column.key)) return { minWidth: "230px" };
  return { minWidth: "150px" };
};

const DynamicTable = ({ columns, rows }) => {
  const { t } = useTranslation("translations");

  const paginatorTemplate = {
    layout: "CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink",
    CurrentPageReport: (options) => {
      return (
        <span
          style={{
            color: "var(--text-color)",
            userSelect: "none",
            width: "120px",
            textAlign: "center",
          }}
        >
          {options.first} - {options.last} {t("chatbot.of")} {options.totalRecords}
        </span>
      );
    },
  };
  const effectiveColumns = useMemo(() => {
    if (!rows || rows.length === 0) {
      return [];
    }

    // If columns are provided from API, use them with their label field
    if (columns && Array.isArray(columns) && columns.length > 0) {
      return columns
        .filter((col) => !col.hidden && !hiddenFields.includes(col.key))
        .map((col) => ({
          key: col.key,
          label: col.label || knownLabels[col.key] || func.camelToFlat(col.key),
          type: resolveColumnType(col, rows[0]?.[col.key]),
        }));
    }

    // Fallback: generate columns from row data
    const firstRow = rows[0];
    const generatedColumns = [];

    Object.keys(firstRow).forEach((key) => {
      if (hiddenFields.includes(key)) {
        return;
      }

      const label = knownLabels[key] || func.camelToFlat(key);
      const type = inferColumnType(key, firstRow[key]);

      generatedColumns.push({
        key,
        label,
        type,
      });
    });

    return generatedColumns;
  }, [columns, rows]);

  const formatCellValue = (value, columnType) => {
    if (value === null || value === undefined) {
      return "-";
    }

    switch (columnType) {
      case "date":
        return moment(value).isValid() ? moment(value).format("DD/MM/YYYY") : String(value);

      case "datetime":
        return moment(value).isValid()
          ? moment(value).format("DD/MM/YYYY HH:mm")
          : String(value);

      case "money":
        return formatMoney(value, func.getFormattedMoneyValue);

      case "enum":
        return formatStatusLabel(value);

      case "boolean":
        return value ? t("chatbot.yes") : t("chatbot.no");

      case "string":
      default:
        return String(value);
    }
  };

  const bodyTemplate = (rowData, column) => {
    const value = rowData[column.key];
    const formattedValue = formatCellValue(value, column.type);

    if (column.type === "enum") {
      return (
        <span
          className={`status-badge status-${getStatusTone(value)}`}
          title={formattedValue}
        >
          {formattedValue}
        </span>
      );
    }

    if (column.type === "money") {
      const currency = getRowCurrency(rowData);
      const showCurrency =
        currency && !String(formattedValue).toUpperCase().endsWith(String(currency).toUpperCase());
      return (
        <span className="money-value">
          <span className="money-amount">{formattedValue}</span>
          {showCurrency && <span className="money-currency">{currency}</span>}
        </span>
      );
    }

    if (column.type === "currency") {
      return <span className="currency-code">{formattedValue}</span>;
    }

    if (column.type === "boolean") {
      return (
        <span className={`boolean-badge ${value ? "is-true" : "is-false"}`}>
          {formattedValue}
        </span>
      );
    }

    if (isIdentifierField(column.key)) {
      return (
        <span className="identifier-value" title={formattedValue}>
          {formattedValue}
        </span>
      );
    }

    return formattedValue;
  };

  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <div className="dynamic-table">
      <DataTable
        value={rows}
        paginator
        paginatorTemplate={paginatorTemplate}
        rows={5}
        emptyMessage={t("chatbot.noDataFound")}
        className="chatbot-datatable"
        size="small"
      >
        {effectiveColumns.map((column) => (
          <Column
            key={column.key}
            field={column.key}
            header={column.label}
            body={(rowData) => bodyTemplate(rowData, column)}
            sortable
            bodyClassName={getColumnClass(column)}
            headerClassName={getColumnClass(column)}
            style={getColumnStyle(column)}
          />
        ))}
      </DataTable>
    </div>
  );
};

export default DynamicTable;
